'use client'

import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { MiniTopic } from '@deepguide-ai/types'
import type { UIMessage } from 'ai'
import { cn } from '@/lib/utils'
import { Chat } from './chat'
import {
  getSuccessfulToolNames,
  invalidateQueriesForChatTools,
} from './chatQueryInvalidation'

interface ChatPanelProps {
  topic?: MiniTopic
  docId?: string
  className?: string
  onClose?: () => void
  api?: string
  requestBody?: Record<string, unknown>
  /** Called at send-time to snapshot the current page content as markdown */
  getPageContent?: () => string | undefined
  sendOnlyLastMessage?: boolean
  mode?: 'sidebar' | 'floating'
  onToggleMode?: () => void
  onToggleExpand?: () => void
  isExpanded?: boolean
  onMessagesChange?: (messages: UIMessage[]) => void
  /** Called when the AI stream finishes (server has completed all post-processing) */
  onStreamFinish?: () => void
  hideHeader?: boolean
  hideCloseButton?: boolean
  /**
   * The workspace shell can identify chat home synchronously from the URL.
   * This prevents a one-frame flash of the compact sidebar empty state while
   * route-scoped atoms and page context catch up after navigation.
   */
  forceWelcomeLayout?: boolean
  /**
   * Auto-start trigger. Plain string = send that text. Object form lets
   * callers attach a per-call `body` override (e.g., one-shot `chatKind`)
   * to the first send. See chat.tsx for details.
   */
  autoStartMessage?:
    | string
    | { message: string; body?: Record<string, unknown> }
}

export function ChatPanel({
  topic,
  docId,
  className,
  onClose,
  api,
  requestBody,
  getPageContent,
  sendOnlyLastMessage,
  mode,
  onToggleMode,
  onToggleExpand,
  isExpanded,
  onMessagesChange,
  onStreamFinish,
  hideHeader,
  hideCloseButton,
  forceWelcomeLayout,
  autoStartMessage,
}: ChatPanelProps) {
  const queryClient = useQueryClient()

  const handleStreamFinish = useCallback(
    (message: UIMessage) => {
      const invalidation = invalidateQueriesForChatTools(
        queryClient,
        getSuccessfulToolNames(message)
      )
      onStreamFinish?.()
      void invalidation
    },
    [onStreamFinish, queryClient]
  )

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-1 flex-col bg-card',
        'border-l border-border',
        className,
      )}
      data-screenata-chat-root
    >
      <Chat
        topic={topic}
        docId={docId}
        generationStatus='Idle'
        status='ready'
        messages={[]}
        generationError={null}
        onClose={() => onClose?.()}
        api={api}
        requestBody={requestBody}
        getPageContent={getPageContent}
        sendOnlyLastMessage={sendOnlyLastMessage}
        mode={mode}
        onToggleMode={onToggleMode}
        onToggleExpand={onToggleExpand}
        isExpanded={isExpanded}
        onMessagesChange={onMessagesChange}
        onStreamFinish={handleStreamFinish}
        hideHeader={hideHeader}
        hideCloseButton={hideCloseButton}
        forceWelcomeLayout={forceWelcomeLayout}
        autoStartMessage={autoStartMessage}
      />
    </div>
  )
}
